import React from 'react';
import { themes, pageTypeMeta } from './themes';
import { Baby, Heart, Calendar, Camera, PenLine, CheckSquare, Stethoscope } from 'lucide-react';

const PregnancyPage = ({ page, pageIndex, totalPages, theme }) => {
  const t = themes[theme] || themes['modern-minimal'];
  const meta = pageTypeMeta[page.pageType] || { label: 'Pregnancy', color: t.accent };

  const renderWeekly = () => {
    const weekFields = page.weekFields || page.fields || [];
    const weekNum = page.weekNumber || '';
    const babySize = page.babySize || '';
    const trimester = page.trimester || '';
    return (
      <div className="flex-1 overflow-hidden">
        {/* Week header */}
        <div className="flex items-center justify-between mb-3 pb-2" style={{ borderBottom: `1px solid ${t.accent}15` }}>
          {weekNum && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: t.accent + '15' }}>
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.7rem', fontWeight: 800, color: t.accent }}>{weekNum}</span>
              </div>
              <div>
                <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontWeight: 600, color: t.text }}>Week {weekNum}</span>
                {trimester && <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.subtext, marginLeft: '4px' }}>Trimester {trimester}</span>}
              </div>
            </div>
          )}
          {babySize && (
            <span className="text-[8px] px-2 py-0.5 rounded-full" style={{ backgroundColor: t.accentLight || t.accent + '08', color: t.accent, fontFamily: t.bodyFont }}>
              Baby: {babySize}
            </span>
          )}
        </div>
        {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
        <div className="space-y-2">
          {weekFields.map((f, fi) => (
            <div key={fi}>
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.accent, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{f}</span>
              <div className="mt-0.5 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMilestone = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', fontStyle: 'italic', color: t.subtext, lineHeight: 1.5 }}>{page.content}</p>}
      <div className="space-y-2">
        {(page.fields || ['Date', 'What happened', 'How I felt', 'Who was there']).map((f, fi) => (
          <div key={fi}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <Heart className="w-2 h-2" style={{ color: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.accent }}>{f}</span>
            </div>
            <div className={`rounded-lg ${f.toLowerCase().includes('felt') || f.toLowerCase().includes('felt') ? 'h-8' : 'h-5'}`} style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderPhoto = () => (
    <div className="flex-1 flex flex-col">
      <div className="rounded-xl flex flex-col items-center justify-center mb-3" style={{ backgroundColor: t.fieldBg || t.bg, border: `2px dashed ${t.accent}25`, minHeight: '8rem' }}>
        <Camera className="w-6 h-6 mb-1.5" style={{ color: t.accent + '40' }} strokeWidth={1.5} />
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.5rem', color: t.accent + '60' }}>Bump photo</span>
      </div>
      <div className="space-y-1.5">
        {(page.fields || ['Week', 'Date', 'How I feel']).map((f, fi) => (
          <div key={fi} className="flex items-center gap-2">
            <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, minWidth: '3rem' }}>{f}:</span>
            <div className="flex-1 h-4 rounded" style={{ borderBottom: `1px dashed ${t.accent}25` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderLetter = () => {
    const prompt = page.letterPrompt || '';
    return (
      <div className="flex-1 flex flex-col">
        {prompt && (
          <div className="mb-3 px-3 py-2 rounded-xl" style={{ backgroundColor: t.accentLight || t.accent + '08' }}>
            <p style={{ fontFamily: t.font, fontSize: '0.7rem', fontStyle: 'italic', color: t.accent, lineHeight: 1.5 }}>{prompt}</p>
          </div>
        )}
        <div className="space-y-3.5 flex-1">
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} className="h-px w-full" style={{ backgroundColor: t.fieldBorder || t.accent + '12' }} />
          ))}
        </div>
      </div>
    );
  };

  const renderChecklist = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2" style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', fontStyle: 'italic', color: t.subtext }}>{page.content}</p>}
      <div className="space-y-1">
        {(page.fields || []).map((f, fi) => (
          <div key={fi} className="flex items-center gap-2">
            <div className="w-3.5 h-3.5 rounded border flex-shrink-0" style={{ borderColor: t.accent + '40' }} />
            <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.text }}>{f}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAppointment = () => (
    <div className="flex-1 overflow-hidden">
      <div className="space-y-2">
        {(page.fields || ['Date', 'Doctor/Midwife', 'Week', 'Weight', 'Blood pressure', 'Baby heart rate', 'Notes', 'Questions']).map((f, fi) => (
          <div key={fi}>
            <div className="flex items-center gap-1.5 mb-0.5">
              <Stethoscope className="w-2 h-2" style={{ color: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', fontWeight: 600, color: t.accent }}>{f}</span>
            </div>
            <div className={`rounded-lg ${f.toLowerCase().includes('notes') || f.toLowerCase().includes('questions') ? 'h-8' : 'h-5'}`} style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderDefault = () => (
    <div className="flex-1 overflow-hidden">
      {page.content && <p className="mb-2 leading-relaxed" style={{ fontFamily: t.bodyFont, fontSize: '0.65rem', color: t.subtext, lineHeight: 1.6 }}>{page.content}</p>}
      {page.fields && page.fields.length > 0 && (
        <div className="space-y-1.5">
          {page.fields.map((f, fi) => (
            <div key={fi} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.accent }} />
              <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.text, fontWeight: 500 }}>{f}</span>
              <div className="flex-1 h-5 rounded-lg" style={{ backgroundColor: t.fieldBg || t.bg, border: `1px solid ${t.fieldBorder || t.accent + '15'}` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const contentMap = {
    'pregnancy-weekly': renderWeekly,
    'pregnancy-milestone': renderMilestone,
    'pregnancy-photo': renderPhoto,
    'pregnancy-letter': renderLetter,
    'pregnancy-checklist': renderChecklist,
    'pregnancy-wishlist': renderChecklist,
    'pregnancy-appointment': renderAppointment,
    'pregnancy-birth': renderAppointment,
  };

  const render = contentMap[page.pageType] || renderDefault;

  return (
    <div data-testid={`document-page-${pageIndex}`} className="w-full aspect-[210/297] flex flex-col relative overflow-hidden" style={{ backgroundColor: t.cardBg }}>
      <div className="h-1 w-full" style={{ background: `linear-gradient(to right, ${meta.color}40, ${t.accent}40)` }} />
      <div className="px-7 pt-4 pb-2 flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Baby className="w-3 h-3" style={{ color: meta.color }} />
            <span className="text-[8px] font-bold tracking-[0.15em] uppercase" style={{ fontFamily: t.bodyFont, color: meta.color }}>{meta.label}</span>
          </div>
          <h2 style={{ fontFamily: t.font, fontWeight: 700, fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', color: t.text, lineHeight: 1.2 }}>{page.title}</h2>
        </div>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.6rem', color: t.subtext, opacity: 0.5 }}>{pageIndex + 1}/{totalPages}</span>
      </div>
      <div className="px-7 flex-1 overflow-hidden flex flex-col">{render()}</div>
      <div className="px-7 py-2 flex items-center justify-between" style={{ borderTop: `1px solid ${meta.color}10` }}>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4, letterSpacing: '0.1em' }}>LUMERA STUDIO</span>
        <span style={{ fontFamily: t.bodyFont, fontSize: '0.55rem', color: t.subtext, opacity: 0.4 }}>Page {pageIndex + 1}</span>
      </div>
    </div>
  );
};

export default PregnancyPage;
